import type { ChartDefinition, ChartData, ChartSize } from "./types";
import { renderBarChart } from "./bar-chart";
import { renderLineChart } from "./line-chart";
import { renderPieChart } from "./pie-chart";
import { renderScoreCard } from "./scorecard";
import { renderStackedBarLineChart } from "./stacked-bar-line-chart";
import { renderAreaChart } from "./area-chart";
import { renderCalendarChart } from "./calendar-chart";
import { renderGaugeChart } from "./gauge-chart";
import { renderRadarChart } from "./radar-chart";
import { renderMapChart } from "./map-chart";
import { renderCorrelationChart } from "./correlation-chart";
import { renderScatterChart } from "./scatter-chart";

export const renderChart = async (
    container: HTMLElement | null,
    data: ChartData,
    definition: ChartDefinition,
    size?: ChartSize,
    theme: 'light' | 'dark' = 'light'
): Promise<HTMLElement | null> => {
    // Early return for SSR
    if (typeof window === "undefined") {
        return null;
    }

    if (definition.type === "bar") {
        return renderBarChart(container, data, definition.config, size, theme);
    }
    if (definition.type === "line") {
        return renderLineChart(container, data, definition.config, size, theme);
    }
    if (definition.type === "stacked-line") {
        return renderLineChart(
            container,
            data,
            { ...definition.config, stacked: true },
            size,
            theme
        );
    }
    if (definition.type === "pie") {
        return renderPieChart(container, data, definition.config, size, theme);
    }
    if (definition.type === "scorecard") {
        return renderScoreCard(container, data, definition.config, size, theme);
    }
    if (definition.type === "stacked-bar-line") {
        return renderStackedBarLineChart(container, data, definition.config, size, theme);
    }
    if (definition.type === "area") {
        return renderAreaChart(container, data, definition.config, size, theme);
    }
    if (definition.type === "calendar") {
        return renderCalendarChart(container, data, definition.config, size, theme);
    }
    if (definition.type === "gauge") {
        return renderGaugeChart(container, data, definition.config, size, theme);
    }
    if (definition.type === "radar") {
        return renderRadarChart(container, data, definition.config, size, theme);
    }
    if (definition.type === "map") {
        return renderMapChart(container, data, definition.config, size, theme);
    }
    if (definition.type === "correlation") {
        return renderCorrelationChart(container, data, definition.config, size, theme);
    }
    if (definition.type === "scatter") {
        return renderScatterChart(container, data, definition.config, size, theme);
    }

    throw new Error(`Unsupported chart type: ${definition.type}`);
};

// Export individual chart renderers for direct use if needed
export { renderBarChart } from "./bar-chart";
export { renderLineChart } from "./line-chart";
export { renderPieChart } from "./pie-chart";
export { renderScoreCard } from "./scorecard";
export { renderStackedBarLineChart } from "./stacked-bar-line-chart";
export { renderAreaChart } from "./area-chart";
export { renderCalendarChart } from "./calendar-chart";
export { renderGaugeChart } from "./gauge-chart";
export { renderRadarChart } from "./radar-chart";
export { renderMapChart } from "./map-chart";
export { renderCorrelationChart } from "./correlation-chart";
export { renderScatterChart } from "./scatter-chart";

// Export types
export type { ChartDefinition, ChartData, ChartSize } from "./types";
